// Amplitude analytics — consent-gated (statistics) Browser SDK wrapper.
// The SDK is dynamically imported so it never ships to users who don't
// consent. Event names follow the "Object Action" Title Case convention
// (see docs/analytics-events.md).

import { isEmbedded } from "@/lib/embedded";

// NEXT_PUBLIC_AMPLITUDE_ID is the shared key name used across Thérapie
// projects (e.g. tc-health); NEXT_PUBLIC_AMPLITUDE_API_KEY is kept as an alias.
const API_KEY =
  process.env.NEXT_PUBLIC_AMPLITUDE_ID ||
  process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;
// Match the project's data centre. The shared project is US (Amplitude's
// default); set NEXT_PUBLIC_AMPLITUDE_SERVER_ZONE=EU for an EU-region project.
const SERVER_ZONE =
  process.env.NEXT_PUBLIC_AMPLITUDE_SERVER_ZONE === "EU" ? "EU" : "US";

export const ANALYTICS_EVENTS = {
  CTA_CLICKED: "CTA Clicked",
  NAVIGATION_CLICKED: "Navigation Clicked",
  SEARCH_PERFORMED: "Search Performed",
  SEARCH_RESULT_CLICKED: "Search Result Clicked",
  BOOKING_STARTED: "Booking Started",
  BOOKING_STEP_COMPLETED: "Booking Step Completed",
  BOOKING_SUBMITTED: "Booking Submitted",
  FORM_VIEWED: "Form Viewed",
  CONTACT_FORM_SUBMITTED: "Contact Form Submitted",
  FAQ_WIDGET_OPENED: "FAQ Widget Opened",
  FAQ_TOPIC_SELECTED: "FAQ Topic Selected",
  FAQ_OPENED: "FAQ Opened",
  VIDEO_PLAYED: "Video Played",
  PODCAST_PLAYED: "Podcast Played",
  PHONE_CLICKED: "Phone Clicked",
  OUTBOUND_LINK_CLICKED: "Outbound Link Clicked",
} as const;

export type AnalyticsEvent =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type EventProps = Record<
  string,
  string | number | boolean | undefined | null
>;

type AmplitudeModule = typeof import("@amplitude/analytics-browser");

let sdk: AmplitudeModule | null = null;
let initialized = false;
let initPromise: Promise<void> | null = null;
let queue: Array<{ event: AnalyticsEvent; props?: EventProps }> = [];

export function analyticsEnabled(): boolean {
  return Boolean(API_KEY);
}

function clean(props?: EventProps): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!props) return out;
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined && value !== null && value !== "") out[key] = value;
  }
  return out;
}

export async function initAnalytics(userProps?: EventProps): Promise<void> {
  if (!API_KEY || typeof window === "undefined" || isEmbedded()) return;
  if (initialized) {
    sdk?.setOptOut(false);
    if (userProps) setUserProperties(userProps);
    return;
  }
  if (initPromise) return initPromise;

  initPromise = (async () => {
    sdk = await import("@amplitude/analytics-browser");
    sdk.init(API_KEY, {
      // Data residency — must match the project's region (see SERVER_ZONE).
      serverZone: SERVER_ZONE,
      autocapture: {
        sessions: true,
        attribution: true,
        // Emits "[Amplitude] Page Viewed" (shown as "Viewed <title> Page")
        // and handles SPA route changes — replaces a manual page-view event.
        pageViews: true,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: false,
      },
    });
    initialized = true;
    if (userProps) setUserProperties(userProps);
    const pending = queue;
    queue = [];
    for (const item of pending) sdk.track(item.event, clean(item.props));
  })();

  return initPromise;
}

export function track(event: AnalyticsEvent, props?: EventProps): void {
  if (!API_KEY || typeof window === "undefined" || isEmbedded()) return;
  if (!initialized || !sdk) {
    // Buffer until consent grants init; capped so it can't grow unbounded.
    if (queue.length < 50) queue.push({ event, props });
    return;
  }
  sdk.track(event, clean(props));
}

export function setUserProperties(props: EventProps): void {
  if (!initialized || !sdk) return;
  const identify = new sdk.Identify();
  for (const [key, value] of Object.entries(clean(props))) {
    identify.set(key, value);
  }
  sdk.identify(identify);
}

// Consent withdrawn: stop sending without tearing down the instance.
export function disableAnalytics(): void {
  queue = [];
  sdk?.setOptOut(true);
}
